import { useEffect, useMemo, useState } from "react";
import Button from "../../components/commons/Button";
import {
  buildRejectionReasonPayload,
  countSelectedRejectionFields,
  createEmptyRejectionForm,
  getRejectionFieldLabel,
  getRejectionFieldsForRole,
  getRejectionStepsForRole,
  getRejectionStepLabel,
  hasValidRejectionSelection,
  type RejectionFormState,
  type RejectionReasonPayload,
} from "../../services/webServices/sepehr/authorization-credentials/rejection-reason";

interface RejectReasonModalProps {
  open: boolean;
  loading: boolean;
  authorizeUserRole: "0" | "1" | "2";
  onConfirm: (payload: RejectionReasonPayload) => void;
  onClose: () => void;
}

export default function RejectReasonModal({
  open,
  loading,
  authorizeUserRole,
  onConfirm,
  onClose,
}: RejectReasonModalProps) {
  const rejectionFields = useMemo(
    () => getRejectionFieldsForRole(authorizeUserRole),
    [authorizeUserRole],
  );
  const rejectionSteps = useMemo(
    () => getRejectionStepsForRole(authorizeUserRole),
    [authorizeUserRole],
  );

  const [activeStep, setActiveStep] = useState<number>(rejectionSteps[0]);
  const [form, setForm] = useState<RejectionFormState>(() =>
    createEmptyRejectionForm(rejectionFields),
  );

  useEffect(() => {
    if (!open) return;

    setActiveStep(rejectionSteps[0]);
    setForm(createEmptyRejectionForm(rejectionFields));
  }, [open, rejectionFields, rejectionSteps]);

  if (!open) return null;

  const activeStepIndex = rejectionSteps.indexOf(activeStep);
  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === rejectionSteps.length - 1;
  const selectedCount = countSelectedRejectionFields(form);
  const canSubmit = hasValidRejectionSelection(form);

  const stepFields = rejectionFields.filter((field) => field.step === activeStep);

  const handleToggleField = (key: string, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      [key]: {
        checked,
        reason: checked ? prev[key]?.reason || "" : "",
      },
    }));
  };

  const handleReasonChange = (key: string, reason: string) => {
    setForm((prev) => ({
      ...prev,
      [key]: {
        checked: true,
        reason,
      },
    }));
  };

  const handlePreviousStep = () => {
    if (!isFirstStep) {
      setActiveStep(rejectionSteps[activeStepIndex - 1]);
    }
  };

  const handleNextStep = () => {
    if (!isLastStep) {
      setActiveStep(rejectionSteps[activeStepIndex + 1]);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onConfirm(buildRejectionReasonPayload(form, authorizeUserRole));
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-lg"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        <div className="border-b border-gray-100 px-6 py-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-gray-800">رد درخواست احراز هویت</h3>
              <p className="mt-1 text-sm text-gray-500">
                فیلدهای دارای مشکل را انتخاب کنید و دلیل رد هر مورد را بنویسید.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
            >
              ✕
            </button>
          </div>

          {rejectionSteps.length > 1 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {rejectionSteps.map((step, index) => {
                const isActive = step === activeStep;
                const isCompleted = index < activeStepIndex;
                const stepSelectedCount = rejectionFields.filter(
                  (field) =>
                    field.step === step &&
                    form[field.key]?.checked &&
                    form[field.key]?.reason.trim(),
                ).length;

                return (
                  <button
                    key={step}
                    type="button"
                    disabled={loading}
                    onClick={() => setActiveStep(step)}
                    className={`flex min-w-fit items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                      isActive
                        ? "border-red-300 bg-red-50 text-red-700"
                        : isCompleted
                          ? "border-green-200 bg-green-50 text-green-700"
                          : "border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                        isActive
                          ? "bg-red-500 text-white"
                          : isCompleted
                            ? "bg-green-500 text-white"
                            : "bg-gray-300 text-white"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span>{getRejectionStepLabel(step)}</span>
                    {stepSelectedCount > 0 && (
                      <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] text-white">
                        {stepSelectedCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="mb-4 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {activeStep === 2 &&
              "در این مرحله اطلاعات متنی درخواست بررسی می‌شود. موارد نادرست را علامت بزنید."}
            {activeStep === 3 &&
              "در این مرحله تصاویر کارت ملی و حکم کارگزینی بررسی می‌شوند."}
            {activeStep === 4 &&
              "در این مرحله فایل رزومه و توضیحات مربوط به آن بررسی می‌شود."}
          </div>

          <div className="space-y-3">
            {stepFields.map(({ key }) => {
              const fieldState = form[key] || { checked: false, reason: "" };

              return (
                <div
                  key={key}
                  className={`rounded-xl border transition-colors ${
                    fieldState.checked
                      ? "border-red-200 bg-red-50/40"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <label className="flex cursor-pointer items-start gap-3 p-4">
                    <input
                      type="checkbox"
                      checked={fieldState.checked}
                      disabled={loading}
                      onChange={(e) => handleToggleField(key, e.target.checked)}
                      className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-red-500 focus:ring-red-400"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-800">
                        {getRejectionFieldLabel(key)}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        در صورت وجود مشکل، این مورد را انتخاب کنید.
                      </p>
                    </div>
                  </label>

                  {fieldState.checked && (
                    <div className="border-t border-red-100 px-4 pb-4">
                      <label className="mb-1.5 block text-xs font-medium text-red-700">
                        دلیل رد {getRejectionFieldLabel(key)}
                      </label>
                      <textarea
                        value={fieldState.reason}
                        onChange={(e) => handleReasonChange(key, e.target.value)}
                        placeholder={`توضیح دهید چرا ${getRejectionFieldLabel(key)} رد شده است...`}
                        rows={3}
                        disabled={loading}
                        className="w-full resize-none rounded-xl border border-red-200 bg-white px-4 py-3 text-sm text-gray-800 outline-none transition-colors focus:border-red-400 disabled:bg-gray-50"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t border-gray-100 px-6 py-4">
          <div className="mb-3 flex items-center justify-between text-xs text-gray-500">
            <span>
              {selectedCount > 0
                ? `${selectedCount} مورد برای رد انتخاب شده`
                : "حداقل یک مورد باید انتخاب شود"}
            </span>
            <span>
              مرحله {activeStepIndex + 1} از {rejectionSteps.length}
            </span>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button
              variant="primary"
              style="outline"
              size="md"
              text="انصراف"
              loading={false}
              disabled={loading}
              onClick={onClose}
            />

            <div className="flex flex-wrap items-center gap-3">
              {!isFirstStep && (
                <Button
                  variant="primary"
                  style="outline"
                  size="md"
                  text="مرحله قبل"
                  loading={false}
                  disabled={loading}
                  onClick={handlePreviousStep}
                />
              )}

              {!isLastStep && (
                <Button
                  variant="primary"
                  style="solid"
                  size="md"
                  text="مرحله بعد"
                  loading={false}
                  disabled={loading}
                  onClick={handleNextStep}
                />
              )}

              <Button
                variant="primary"
                style="solid"
                size="md"
                text={`ثبت رد${selectedCount > 0 ? ` (${selectedCount} مورد)` : ""}`}
                loading={loading}
                disabled={loading || !canSubmit}
                onClick={handleSubmit}
                customClassName="bg-red-500 hover:bg-red-600 disabled:bg-red-400"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
